<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <div class="form-section group">
      <span class="icon"><icon-colored-daypart /></span>
      {{ capitalize($t('names.daypart', { n: 2 })) }}
      <div
        v-if="canBeRemoved"
        class="actions">
        <button
          class="btn btn-danger btn-sm"
          @click="clear">
          <icon-delete /> {{ capitalize($t('actions.remove')) }}
        </button>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="isMounted"
        class="form-section-content">
        <div class="form-group row">
          <div
            id="edit_creative_daypart"
            class="col-sm-12">
            <daypart-editor
              :use-precise-daypart="canCreativeUsePreciseDaypart"
              :daypart="daypart"
              @changed="onDaypartChanged" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script type="text/babel" lang="ts">
  import { capitalize } from 'vue';
  import { mapActions } from 'vuex';

  import DaypartEditor from '@/common/components/controls/daypartEditor/DaypartEditor.vue';
  import IconColoredDaypart from '@/common/icons/colored/Daypart.vue';
  import IconDelete from '@/common/icons/monochrome/Delete.vue';
  import Creative from '@/common/models/creative/Creative';
  import {
    convertPreciseToStandardDaypart,
    convertStandardDaypartToPrecise,
    EmptyPreciseDaypart,
    FullDaypart,
    FullPreciseDaypart,
    isPreciseDayPart,
  } from '@/globals';

  export default {
    components: {
      DaypartEditor,
      IconColoredDaypart,
      IconDelete,
    },
    props: {
      creative: {
        type: Creative,
        default: null,
      },
      canBeRemoved: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isMounted: false,
        isDaypartExpanded: true,
        daypartInternal: FullPreciseDaypart,
        capitalize,
      };
    },
    computed: {
      daypart() {
        if (this.isCreativeUsingPreciseDaypart) {
          return this.creative.preciseDaypart;
        }

        return this.canCreativeUsePreciseDaypart
          ? convertStandardDaypartToPrecise(this.creative.daypart)
          : this.creative.daypart;
      },
      canCreativeUsePreciseDaypart() {
        // if the creative was created with a standard daypart and preciseDaypart was not set, the API won't let us update
        // now, we set both daypart and precise daypart to give the user more flexibility
        // this piece of code will support creative created before this fix
        return !!this.creative.preciseDaypart;
      },
      isCreativeUsingPreciseDaypart() {
        return (
          !!this.creative.preciseDaypart &&
          this.creative.preciseDaypart !== FullPreciseDaypart &&
          this.creative.preciseDaypart !== EmptyPreciseDaypart
        );
      },
    },
    mounted() {
      this.isMounted = true;
    },
    unmounted() {
      this.isMounted = false;
    },
    methods: {
      ...mapActions(['updateCreativeDaypart', 'updateCreativePreciseDaypart']),
      onDaypartChanged(daypart: string) {
        if (isPreciseDayPart(daypart)) {
          try {
            const standardDaypart = convertPreciseToStandardDaypart(daypart);
            this.updateCreativePreciseDaypart({ creative: this.creative, value: daypart });
            this.updateCreativeDaypart({ creative: this.creative, value: standardDaypart });
          } catch (error) {
            this.updateCreativePreciseDaypart({ creative: this.creative, value: daypart });
            this.updateCreativeDaypart({ creative: this.creative, value: undefined });
          }
        } else {
          this.updateCreativePreciseDaypart({ creative: this.creative, value: undefined });
          this.updateCreativeDaypart({ creative: this.creative, value: daypart });
        }
        this.$emit('changed', daypart);
      },
      clear() {
        if (this.creative.preciseDaypart != null) {
          this.updateCreativePreciseDaypart({ creative: this.creative, value: FullPreciseDaypart });
        }
        this.updateCreativeDaypart({ creative: this.creative, value: FullDaypart });
        this.$emit('cleared');
      },
    },
  };
</script>
